import FeatureGates from '@atlaskit/feature-gate-js-client';
import Lozenge from '@atlaskit/lozenge';
import { type ThemeColorModes, useThemeObserver } from '@atlaskit/tokens';
import { CROSS_FLOW_MODAL_WIDTH, CROSS_FLOW_MODAL_HEIGHT } from '@atlassian/cross-flow-next-ui';
import { FullScreenModalTrigger } from '@atlassian/entry-points/full-screen-modal-trigger';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { usePostOfficeContext } from '@atlassian/post-office-context';
import { AdControls } from '@post-office/components--ad-controls';
import { crossFlowModalEntryPoint } from '@post-office/components--cross-flow-modal';
import { getProjectCreationContextParams } from '@post-office/components--cross-flow-modal/project-context';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { JpdPrioritizationDarkIcon } from '../../assets/jpd-prioritization-dark';
import { JpdPrioritizationLightIcon } from '../../assets/jpd-prioritization-light';
import { JpdRoadmapDarkIcon } from '../../assets/jpd-roadmap-dark';
import { JpdRoadmapLightIcon } from '../../assets/jpd-roadmap-light';
import { JsmItsmDarkIcon } from '../../assets/jsm-itsm-dark';
import { JsmItsmLightIcon } from '../../assets/jsm-itsm-light';
import { JsmWorkRequestsDarkIcon } from '../../assets/jsm-work-requests-dark';
import { JsmWorkRequestsLightIcon } from '../../assets/jsm-work-requests-light';
import {
	COMPANY_MANAGED_GENERAL_SERVICE_PROJECT,
	JIRA_POLARIS_DISCOVERY,
	JIRA_POLARIS_PRIORITIZATION,
	JIRA_POLARIS_ROADMAP,
	SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED,
	SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC,
} from '../../implicit-message';
import { MenuItemBase } from '../menu-item/menu-item';
import { type JSX } from 'react/jsx-runtime';

type ProductKey = typeof Targets.JIRA_SERVICE_DESK | typeof Targets.JIRA_PRODUCT_DISCOVERY;

const JSMGROWTH_462_EXPERIMENT_KEY = 'jsmgrowth_462_improve_switcher_signups_v2';

interface PlacementRenderProps {
	dismiss: () => void;
}
interface MessageProps {
	productKey: ProductKey;
	entityId: string;
	recommendationSessionId: string;
}

export type Nav4RecommendationProps = MessageProps & PlacementRenderProps;

const itsmLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JsmItsmLightIcon /> : <JsmItsmDarkIcon />;
};

const workRequestLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JsmWorkRequestsLightIcon /> : <JsmWorkRequestsDarkIcon />;
};

const jpdRoadmapLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JpdRoadmapLightIcon /> : <JpdRoadmapDarkIcon />;
};

const jpdPrioritizationLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JpdPrioritizationLightIcon /> : <JpdPrioritizationDarkIcon />;
};

const iconMap: Record<string, (colorMode?: ThemeColorModes) => JSX.Element> = {
	[COMPANY_MANAGED_GENERAL_SERVICE_PROJECT]: workRequestLogoByTheme,
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC]: itsmLogoByTheme,
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED]: itsmLogoByTheme,
	[JIRA_POLARIS_DISCOVERY]: jpdRoadmapLogoByTheme,
	[JIRA_POLARIS_ROADMAP]: jpdRoadmapLogoByTheme,
	[JIRA_POLARIS_PRIORITIZATION]: jpdPrioritizationLogoByTheme,
};

const templateNameMap: Record<string, JSX.Element> = {
	[COMPANY_MANAGED_GENERAL_SERVICE_PROJECT]: (
		<FormattedMessage
			id="cross-flow-work-requests-template.jsm.title"
			defaultMessage="Collect requests"
			description="Jira Service Management Work Requests template display name"
		/>
	),
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC]: (
		<FormattedMessage
			id="cross-flow-itsm-template.jsm.title"
			defaultMessage="Collect IT requests"
			description="Jira Service Management ITSM template display name"
		/>
	),
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED]: (
		<FormattedMessage
			id="cross-flow-itsm-template.jsm.title"
			defaultMessage="Collect IT requests"
			description="Jira Service Management ITSM template display name"
		/>
	),
	[JIRA_POLARIS_DISCOVERY]: (
		<FormattedMessage
			id="cross-flow-product-roadmap-template.jpd.title"
			defaultMessage="Create a roadmap"
			description="Jira Product Discovery product roadmap template display name"
		/>
	),
	[JIRA_POLARIS_ROADMAP]: (
		<FormattedMessage
			id="cross-flow-product-roadmap-template.jpd.title"
			defaultMessage="Create a roadmap"
			description="Jira Product Discovery product roadmap template display name"
		/>
	),
	[JIRA_POLARIS_PRIORITIZATION]: (
		<FormattedMessage
			id="cross-flow-product-prioritization-template.jpd.title"
			defaultMessage="Prioritize ideas"
			description="Jira Product Discovery product prioritization template display name"
		/>
	),
};

const productNameMap: Record<ProductKey, string> = {
	[Targets.JIRA_SERVICE_DESK]: 'Jira Service Management',
	[Targets.JIRA_PRODUCT_DISCOVERY]: 'Jira Product Discovery',
};

export const Nav4Recommendation = ({
	productKey,
	entityId,
	recommendationSessionId,
	dismiss,
}: Nav4RecommendationProps): JSX.Element => {
	const { colorMode } = useThemeObserver();
	const { formatMessage } = useIntl();
	const [ssrSafeColorMode, setSsrSafeColorMode] = useState<ThemeColorModes | undefined>(undefined);
	const icon = iconMap[entityId]?.(ssrSafeColorMode);
	const templateName = templateNameMap[entityId];
	const crossFlow = useCrossFlow();
	const { isSiteAdmin } = usePostOfficeContext();

	const isJsmOrJpdProduct =
		productKey === Targets.JIRA_SERVICE_DESK || productKey === Targets.JIRA_PRODUCT_DISCOVERY;

	const [isModalVariation, setIsModalVariation] = useState(false);

	const crossFlowModalContextParams = useMemo(
		() =>
			getProjectCreationContextParams({
				targetProduct: productKey,
				templateKey: entityId,
				formatMessage,
			}),
		[entityId, formatMessage, productKey],
	);

	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	const { messageInstanceClicked } = useMessageInstanceEvent();

	const adControlsRef = useRef<HTMLDivElement>(null);

	const wrapTriggerRef = useCallback(
		(ref: React.Ref<HTMLElement>) => (el: HTMLDivElement | null) => {
			if (el) {
				const origAdd = el.addEventListener.bind(el);
				const origRemove = el.removeEventListener.bind(el);
				const listenerMap = new WeakMap<EventListener, EventListener>();

				el.addEventListener = function (
					type: string,
					fn: EventListenerOrEventListenerObject | null,
					opts?: boolean | AddEventListenerOptions,
				) {
					if ((type === 'click' || type === 'mousedown') && typeof fn === 'function') {
						const wrapped: EventListener = (e: Event) => {
							if (adControlsRef.current?.contains(e.target as Node)) {
								return;
							}
							fn.call(el, e);
						};
						listenerMap.set(fn, wrapped);
						return origAdd(type, wrapped, opts);
					}
					return origAdd(type, fn as EventListenerOrEventListenerObject, opts);
				};

				el.removeEventListener = function (
					type: string,
					fn: EventListenerOrEventListenerObject | null,
					opts?: boolean | EventListenerOptions,
				) {
					if ((type === 'click' || type === 'mousedown') && typeof fn === 'function') {
						const wrapped = listenerMap.get(fn);
						if (wrapped) {
							listenerMap.delete(fn);
							return origRemove(type, wrapped, opts);
						}
					}
					return origRemove(type, fn as EventListenerOrEventListenerObject, opts);
				};
			}
			if (typeof ref === 'function') {
				ref(el);
			} else if (ref && typeof ref === 'object') {
				(ref as React.MutableRefObject<HTMLElement | null>).current = el;
			}
		},
		[],
	);

	useEffect(() => {
		setSsrSafeColorMode(colorMode);
	}, [colorMode]);

	useEffect(() => {
		setIsModalVariation(
			Boolean(
				isSiteAdmin &&
				isJsmOrJpdProduct &&
				FeatureGates.getExperimentValue<boolean>(JSMGROWTH_462_EXPERIMENT_KEY, 'isEnabled', false, {
					fireExperimentExposure: false,
				}),
			),
		);
	}, [isJsmOrJpdProduct, isSiteAdmin]);

	useEffect(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'projectsMenu',
			},
			{
				source: 'projectsMenuSection',
				recommendedProductIds: [productKey],
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
			},
			{
				sqa1052Enabled: true,
				templateId: entityId,
			},
		);
	}, [entityId, fireViewedUIEvent, productKey, recommendationSessionId]);

	const handleClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'recommendation',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'react',
			},
			additionalMetadataFromFrontend: {
				sqa1052Enabled: true,
				templateId: entityId,
			},
		});

		if (isSiteAdmin && isJsmOrJpdProduct) {
			FeatureGates.manuallyLogExperimentExposure(JSMGROWTH_462_EXPERIMENT_KEY);
		}

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryThisRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: productKey,
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
				recommendedProductIds: [productKey],
			},
			{
				sqa1052Enabled: true,
				templateId: entityId,
			},
		);

		if (!isModalVariation && crossFlow.isEnabled) {
			void crossFlow.apiV1.open({
				v1: true,
				sourceComponent: 'projectsMenu',
				sourceContext: 'tryThisRecommendation',
				targetProduct: productKey,
				journey: Journeys.GET_STARTED,
				extensions: {
					provisioningPageOptions: {
						actions: {
							primary: {
								behaviour: 'land',
								prompt: 'next',
								options: {
									targetTab: 'same',
									urlParams: { templateId: entityId },
								},
							},
						},
					},
					recommendationContext: {
						recommendationSessionId,
						entityId,
					},
				},
			});
		}
	}, [
		crossFlow,
		entityId,
		fireInteractedUIEvent,
		messageInstanceClicked,
		productKey,
		recommendationSessionId,
		isModalVariation,
		isJsmOrJpdProduct,
		isSiteAdmin,
	]);

	const handleDismiss = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				sqa1052Enabled: true,
				templateId: entityId,
			},
		});

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: productKey,
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
				recommendedProductIds: [productKey],
			},
			{
				sqa1052Enabled: true,
				templateId: entityId,
			},
		);
		dismiss();
	}, [
		dismiss,
		entityId,
		messageInstanceClicked,
		recommendationSessionId,
		productKey,
		fireInteractedUIEvent,
	]);

	const itemContent = (
		<MenuItemBase
			ariaDescribedby="try-this-recommendation-lozenge"
			elemBefore={icon}
			elemAfter={
				<Lozenge appearance="new" isBold={false}>
					<span id="try-this-recommendation-lozenge">
						<FormattedMessage
							id="post-office.nav-4-projects-recommendations.cta-button-lozenge-try"
							defaultMessage="Try"
							description="A lozenge to display the try button"
						/>
					</span>
				</Lozenge>
			}
			onClick={handleClick}
			actionsOnHover={
				<div ref={adControlsRef}>
					<AdControls
						iconSize="small"
						spacing="compact"
						onDismissed={handleDismiss}
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							message: (
								<FormattedMessage
									id="post-office.nav-4-projects-recommendations.reasons-modal-explanation"
									defaultMessage="We think {productName} will improve your team's productivity. The recommendation is based on:"
									description="Explanation as to why the placeholder template is being shown"
									values={{ productName: <strong>{productNameMap[productKey]}</strong> }}
								/>
							),
							reasons: [
								<FormattedMessage
									id="post-office.nav-4-projects-recommendations.reasons-modal-bullet-one-spork"
									defaultMessage="the tools you use (Jira)"
									description="First bullet point of the reasons modal"
								/>,
								<FormattedMessage
									id="post-office.nav-4-projects-recommendations.reasons-modal-bullet-two-appify"
									defaultMessage="the way you use your existing tools and apps"
									description="Second bullet point of the reasons modal"
								/>,
							],
						}}
						additionalAnalyticsAttributes={{
							source: 'projectsMenuSection',
							targetProductId: productKey,
							sqa1052Enabled: 'true',
						}}
						ariaLabelledby="nav-4-projects-recommendation-message-title"
					/>
				</div>
			}
		>
			<span id="nav-4-projects-recommendation-message-title">{templateName}</span>
		</MenuItemBase>
	);

	return (
		<div role="listitem">
			{isModalVariation ? (
				<FullScreenModalTrigger
					entryPoint={crossFlowModalEntryPoint}
					entryPointParams={{}}
					entryPointProps={{
						sourceComponent: 'projectsMenu',
						sourceContext: 'tryThisRecommendation',
						targetProduct: productKey,
						shouldRedirectOnSuccess: true,
						fakeBackground: 'jira',
						contextParams: crossFlowModalContextParams,
						width: CROSS_FLOW_MODAL_WIDTH,
						height: CROSS_FLOW_MODAL_HEIGHT,
					}}
					modalProps={{
						shouldCloseOnEscapePress: true,
					}}
				>
					{({ ref }) => <div ref={wrapTriggerRef(ref)}>{itemContent}</div>}
				</FullScreenModalTrigger>
			) : (
				itemContent
			)}
		</div>
	);
};
